import { recordImages } from "@/lib/record-images";
import type { ProjectRecord } from "@/types/api";

/**
 * When a record's **most recent** photo was taken, as an ISO timestamp.
 *
 * The value is a photo's `taken_at` — the server-resolved capture time, which
 * is the EXIF timestamp for an uploaded file and the capture moment for one
 * shot in the app (see `taken_at_source`: `exif`, `file_last_modified`,
 * `user_input`, `upload_time`). A record's photos come back in capture order,
 * so this is the **last** photo of the set, which is the one the record viewer
 * is oriented around; taking the maximum rather than the last array entry keeps
 * it honest if a legacy set ever arrives out of order. Photos with no
 * `taken_at` are skipped rather than treated as the answer.
 *
 * Falls back to when the record row itself was created — legacy rows predating
 * `taken_at`, the public share read (which does not expose it), and queued
 * drafts all land here, and "when this got into Otus" is the closest honest
 * answer. Returns null only when neither is known.
 */
export function recordLatestPhotoAt(
  record: Pick<ProjectRecord, "image" | "images" | "created_at">,
): string | null {
  const photos = recordImages(record as ProjectRecord);
  let latest: string | null = null;
  for (const photo of photos) {
    const taken = photo.taken_at;
    if (!taken) continue;
    // ISO-8601 with the same offset sorts lexicographically, and the server
    // emits UTC — so string comparison is the ordering, no Date parsing needed.
    if (latest === null || taken > latest) latest = taken;
  }
  return latest ?? record.created_at ?? null;
}

/**
 * Render a capture timestamp as `31 Aug 2026` — the same format reports and
 * plan revisions use. Returns null for a missing or unparseable value so
 * callers can drop the line rather than print "Invalid Date".
 */
export function formatCapturedAt(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-SG", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}
